import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye, EyeOff, Save, X, ExternalLink, Upload, Image as ImageIcon } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useToast } from '../../hooks/use-toast';
import { getImageUrlWithFallback } from '../../utils/imageUtils';

const AdminCanvaTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    style: 'classico',
    canvaUrl: '',
    thumbnailUrl: '',
    sortOrder: 0
  });

  const { toast } = useToast();
  const { adminToken } = useAdminAuth();
  const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

  const styles = [
    { value: 'classico', label: 'Classico' },
    { value: 'moderno', label: 'Moderno' },
    { value: 'vintage', label: 'Vintage' },
    { value: 'rustico', label: 'Rustico' },
    { value: 'elegante', label: 'Elegante' }
  ];

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/canva-templates/admin/all`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      toast({
        title: "Errore",
        description: "Impossibile caricare i template",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (!adminToken) {
        toast({
          title: "Errore",
          description: "Sessione scaduta. Effettua nuovamente il login",
          variant: "destructive"
        });
        return;
      }

      // Se c'è un file thumbnail, prima caricalo
      let finalThumbnailUrl = formData.thumbnailUrl;
      
      if (thumbnailFile) {
        const uploadFormData = new FormData();
        uploadFormData.append('file', thumbnailFile);
        
        // Usa un ID temporaneo per il template (verrà sovrascritto dopo)
        const tempId = editingTemplate?.id || `temp_${Date.now()}`;
        
        const uploadResponse = await fetch(`${backendUrl}/api/upload/admin/canva-templates/${tempId}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${adminToken}`
          },
          body: uploadFormData
        });
        
        const uploadResult = await uploadResponse.json();
        
        if (uploadResponse.ok && uploadResult.success) {
          // Usa il filePath restituito dal backend
          finalThumbnailUrl = uploadResult.filePath;
        } else {
          throw new Error(uploadResult.message || uploadResult.detail || 'Errore nel caricamento della thumbnail');
        }
      }

      const url = editingTemplate
        ? `${backendUrl}/api/canva-templates/admin/${editingTemplate.id}`
        : `${backendUrl}/api/canva-templates/admin/create`;

      const dataToSend = {
        ...formData,
        thumbnailUrl: finalThumbnailUrl
      };

      const response = await fetch(url, {
        method: editingTemplate ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify(dataToSend)
      });

      const result = await response.json();

      if (response.ok) {
        toast({
          title: "Successo",
          description: result.message
        });
        setShowModal(false);
        resetForm();
        loadTemplates();
      } else {
        throw new Error(result.message || 'Errore durante il salvataggio');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast({
        title: "Errore",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || '',
      style: template.style,
      canvaUrl: template.canvaUrl,
      thumbnailUrl: template.thumbnailUrl || '',
      sortOrder: template.sortOrder
    });
    setShowModal(true);
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo template?')) return;

    try {
      const response = await fetch(`${backendUrl}/api/canva-templates/admin/${templateId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });

      const result = await response.json();

      if (response.ok) {
        toast({
          title: "Successo",
          description: result.message
        });
        loadTemplates();
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      toast({
        title: "Errore",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleToggleActive = async (templateId) => {
    try {
      const response = await fetch(`${backendUrl}/api/canva-templates/admin/${templateId}/toggle`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });

      const result = await response.json();

      if (response.ok) {
        toast({
          title: "Successo",
          description: result.message
        });
        loadTemplates();
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      toast({
        title: "Errore",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      style: 'classico',
      canvaUrl: '',
      thumbnailUrl: '',
      sortOrder: 0
    });
    setEditingTemplate(null);
    setThumbnailFile(null);
    setThumbnailPreview(null);
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "File troppo grande",
          description: "La thumbnail deve essere massimo 5MB",
          variant: "destructive"
        });
        return;
      }
      
      setThumbnailFile(file);
      // Reset il vecchio URL quando viene caricato un nuovo file
      setFormData({ ...formData, thumbnailUrl: '' });
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setThumbnailPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const getStyleColor = (style) => {
    const colors = {
      classico: 'bg-blue-100 text-blue-800',
      moderno: 'bg-purple-100 text-purple-800',
      vintage: 'bg-amber-100 text-amber-800',
      rustico: 'bg-green-100 text-green-800',
      elegante: 'bg-pink-100 text-pink-800'
    };
    return colors[style] || 'bg-gray-100 text-gray-800';
  };

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Template Canva</h1>
            <p className="text-gray-600 mt-1">Gestisci i template Canva per le etichette</p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5 mr-2" />
            Nuovo Template
          </button>
        </div>

        {/* Templates Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <p className="text-gray-500">Nessun template trovato</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates.map((template) => (
              <div key={template.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                {/* Thumbnail */}
                <div className="h-48 bg-gray-100 flex items-center justify-center overflow-hidden">
                  {template.thumbnailUrl ? (
                    <img 
                      src={getImageUrlWithFallback(template.thumbnailUrl)} 
                      alt={template.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23f3f4f6" width="100" height="100"/%3E%3Ctext fill="%239ca3af" font-family="Arial" font-size="14" x="50%25" y="50%25" text-anchor="middle" dominant-baseline="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
                      }}
                    />
                  ) : (
                    <div className="text-gray-400 text-center p-4">
                      <ExternalLink className="w-12 h-12 mx-auto mb-2" />
                      <p className="text-sm">Nessuna anteprima</p>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-gray-900">{template.name}</h3>
                    <span className={`px-2 py-1 text-xs rounded ${getStyleColor(template.style)}`}>
                      {template.style}
                    </span>
                  </div>
                  
                  {template.description && (
                    <p className="text-sm text-gray-600 mb-3">{template.description}</p>
                  )}

                  <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
                    <span>Ordine: {template.sortOrder}</span>
                    <span className={template.active ? 'text-green-600' : 'text-red-600'}>
                      {template.active ? '● Attivo' : '● Disattivato'}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <a
                      href={template.canvaUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 flex items-center justify-center px-3 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors text-sm"
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      Apri
                    </a>
                    <button
                      onClick={() => handleEdit(template)}
                      className="px-3 py-2 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleToggleActive(template.id)}
                      className="px-3 py-2 bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200 transition-colors"
                    >
                      {template.active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => handleDelete(template.id)}
                      className="px-3 py-2 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold">
                    {editingTemplate ? 'Modifica Template' : 'Nuovo Template'}
                  </h2>
                  <button
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome Template *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="es: Elegante Oro"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Descrizione
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      rows="3"
                      placeholder="Breve descrizione del template..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Stile *
                    </label>
                    <select
                      required
                      value={formData.style}
                      onChange={(e) => setFormData({ ...formData, style: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {styles.map(style => (
                        <option key={style.value} value={style.value}>
                          {style.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      URL Canva *
                    </label>
                    <input
                      type="url"
                      required
                      value={formData.canvaUrl}
                      onChange={(e) => setFormData({ ...formData, canvaUrl: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="https://www.canva.com/design/..."
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Usa l'URL Canva in modalità view/preview
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Thumbnail Anteprima
                    </label>
                    
                    {/* Preview */}
                    {(thumbnailPreview || formData.thumbnailUrl) && (
                      <div className="mb-3">
                        <img 
                          src={thumbnailPreview || getImageUrlWithFallback(formData.thumbnailUrl)} 
                          alt="Preview"
                          className="w-full h-48 object-cover rounded-lg border border-gray-200"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23f3f4f6" width="100" height="100"/%3E%3Ctext fill="%239ca3af" font-family="Arial" font-size="14" x="50%25" y="50%25" text-anchor="middle" dominant-baseline="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
                          }}
                        />
                      </div>
                    )}
                    
                    {/* Upload File */}
                    <label className="block mb-2">
                      <div className="flex items-center justify-center w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 transition-colors bg-gray-50">
                        <Upload className="w-5 h-5 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-600">
                          {thumbnailFile ? thumbnailFile.name : 'Carica immagine (max 5MB)'}
                        </span>
                      </div>
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp"
                        onChange={handleThumbnailChange}
                        className="hidden"
                      />
                    </label>
                    
                    {/* OR separator */}
                    <div className="flex items-center my-3">
                      <div className="flex-1 border-t border-gray-300"></div>
                      <span className="px-3 text-xs text-gray-500">OPPURE</span>
                      <div className="flex-1 border-t border-gray-300"></div>
                    </div>
                    
                    {/* URL Input */}
                    <input
                      type="url"
                      value={formData.thumbnailUrl}
                      onChange={(e) => {
                        setFormData({ ...formData, thumbnailUrl: e.target.value });
                        setThumbnailFile(null);
                        setThumbnailPreview(null);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Inserisci URL immagine"
                      disabled={!!thumbnailFile}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Carica un file o inserisci un URL esterno
                    </p>
                    
                    {/* Remove buttons */}
                    {(thumbnailFile || formData.thumbnailUrl || thumbnailPreview) && (
                      <button
                        type="button"
                        onClick={() => {
                          setThumbnailFile(null);
                          setThumbnailPreview(null);
                          setFormData({ ...formData, thumbnailUrl: '' });
                        }}
                        className="mt-2 text-sm text-red-600 hover:text-red-800"
                      >
                        Rimuovi thumbnail
                      </button>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ordine
                    </label>
                    <input
                      type="number"
                      value={formData.sortOrder}
                      onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0"
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="submit"
                      className="flex-1 flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Save className="w-5 h-5 mr-2" />
                      Salva
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowModal(false);
                        resetForm();
                      }}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                      Annulla
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminCanvaTemplates;
